import React from "react";
import { Box, Paper, Typography } from "@mui/material";
import ScienceIcon from "@mui/icons-material/Science";
import { useTranslation } from "react-i18next";
import color from "../../Constants/colors";

export const formatBytes = (bytes: number | null | undefined): string => {
  if (bytes === null || bytes === undefined || isNaN(bytes)) return "—";
  if (bytes === 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB", "PB"];
  const exponent = Math.min(
    units.length - 1,
    Math.floor(Math.log(bytes) / Math.log(1024))
  );
  const value = bytes / Math.pow(1024, exponent);
  return `${value.toFixed(value >= 100 || exponent === 0 ? 0 : 1)} ${units[exponent]}`;
};

export const formatRate = (bps: number | null | undefined): string => {
  if (bps === null || bps === undefined || isNaN(bps)) return "—";
  return `${formatBytes(bps)}/s`;
};

export const formatUptime = (seconds: number | null | undefined): string => {
  if (seconds === null || seconds === undefined || isNaN(seconds)) return "—";
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (days > 0) return `${days}d ${hours}h ${minutes}m`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

export interface Health {
  color: string;
  bgColor: string;
  labelKey: string;
}

export const getHealth = (percentage: number): Health => {
  if (percentage <= 50)
    return { color: color.green700, bgColor: color.green50, labelKey: "healthy" };
  if (percentage <= 75)
    return {
      color: color.orange500,
      bgColor: color.orange50,
      labelKey: "moderate",
    };
  if (percentage <= 90)
    return { color: color.orange600, bgColor: color.orange50, labelKey: "atRisk" };
  return { color: color.red600, bgColor: color.red50, labelKey: "critical" };
};

export const StatCard: React.FC<{
  title: string;
  value: React.ReactNode;
  icon: React.ReactElement;
  accentColor: string;
  hint?: string;
}> = ({ title, value, icon, accentColor, hint }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2.5,
      borderRadius: "14px",
      flex: "1 1 200px",
      minWidth: 180,
      backgroundColor: color.primary,
      border: `1px solid ${color.gray200}`,
      position: "relative",
      overflow: "hidden",
      transition: "box-shadow 0.2s, transform 0.2s",
      "&:hover": {
        boxShadow: "0 4px 20px rgba(5,77,127,0.10)",
        transform: "translateY(-2px)",
      },
    }}
  >
    <Box
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: 4,
        backgroundColor: accentColor,
        borderRadius: "14px 14px 0 0",
      }}
    />
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: "10px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: `${accentColor}14`,
          color: accentColor,
        }}
      >
        {icon}
      </Box>
      <Typography
        sx={{
          fontSize: "0.7rem",
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          color: color.gray500,
        }}
      >
        {title}
      </Typography>
    </Box>
    <Typography
      sx={{
        fontFamily: "'Montserrat', sans-serif",
        fontWeight: 800,
        fontSize: "1.6rem",
        lineHeight: 1.1,
        color: color.textPrimary,
      }}
    >
      {value}
    </Typography>
    {hint && (
      <Typography sx={{ fontSize: "0.7rem", color: color.gray500, mt: 0.5 }}>
        {hint}
      </Typography>
    )}
  </Paper>
);

export const DummyBadge: React.FC = () => {
  const { t } = useTranslation("monitoring");
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        px: 1.5,
        py: 0.5,
        mb: 1,
        borderRadius: "20px",
        backgroundColor: color.orange50,
        border: `1px solid ${color.accent}55`,
      }}
    >
      <ScienceIcon sx={{ fontSize: "1rem", color: color.accent_dark }} />
      <Typography
        sx={{
          fontSize: "0.7rem",
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          color: color.accent_dark,
        }}
      >
        {t("hardwareDummyBadge")}
      </Typography>
    </Box>
  );
};

/** Thin labelled usage bar (label + percent + colored track), shared by the
 *  per-core CPU grid and the per-service cards. */
export const UsageBar: React.FC<{
  label: string;
  percent: number;
  detail?: string;
}> = ({ label, percent, detail }) => {
  const clamped = Math.max(0, Math.min(100, percent));
  const health = getHealth(clamped);

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          mb: 0.5,
        }}
      >
        <Typography
          sx={{
            fontSize: "0.65rem",
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            color: color.gray500,
          }}
        >
          {label}
        </Typography>
        <Typography
          sx={{
            fontFamily: "'Montserrat', sans-serif",
            fontWeight: 800,
            fontSize: "0.85rem",
            color: health.color,
          }}
        >
          {detail ?? `${clamped.toFixed(0)}%`}
        </Typography>
      </Box>
      <Box
        sx={{
          width: "100%",
          height: 8,
          backgroundColor: color.gray100,
          borderRadius: "4px",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            width: `${clamped}%`,
            height: "100%",
            backgroundColor: health.color,
            borderRadius: "4px",
            transition: "width 0.6s ease, background-color 0.4s ease",
          }}
        />
      </Box>
    </Box>
  );
};
